import ClubCostCenter from './ClubCostCenter.js';
import ClubFeeCategory from './ClubFeeCategory.js';
import ClubImpersonalAccount from './ClubImpersonalAccount.js';
import Department from './Department.js';
import PriceHistory from './PriceHistory.js';

export default class AccountingClubFee {
    static fromData(data) {
        if (!data) {
            return;
        }

        const clubFee = new AccountingClubFee();
        const priceHistory = PriceHistory.fromData(data.priceHistory.data);

        clubFee.id = data.id;
        clubFee.name = data.name;
        clubFee.note = data.note;
        clubFee.paymentApproach = data.paymentApproach;
        clubFee.isArchived = data.isArchived;
        clubFee.clubCostCenter = ClubCostCenter.fromData(data.clubCostCenter);
        clubFee.impersonalAccount = ClubImpersonalAccount.fromData(data.impersonalAccount);
        clubFee.category = ClubFeeCategory.fromData(data.category);
        clubFee.department = Department.fromData(data.department);
        clubFee.basePrice = priceHistory.basePrice;
        clubFee.rollingIntervalType = data.rollingIntervalType;
        clubFee.invoiceTemplateType = data.invoiceTemplateType;

        clubFee.paymentInterval = {
            type: data.paymentInterval.data ? data.paymentInterval.data.type : '',
            monthly: {
                day: data.paymentInterval.data && data.paymentInterval.data.type === 'monthly' ? data.paymentInterval.data.day : 1,
            },
            monthlyEnd: {
                offset: data.paymentInterval.data && data.paymentInterval.data.type === 'monthlyEnd' ? data.paymentInterval.data.offset : 0,
            },
            quarterly: {
                offsetDay: data.paymentInterval.data && data.paymentInterval.data.type === 'quarterly' ? data.paymentInterval.data.offsetDay : 0,
                offsetMonth: data.paymentInterval.data && data.paymentInterval.data.type === 'quarterly' ? data.paymentInterval.data.offsetMonth : 0,
            },
            halfYearly: {
                offsetDay: data.paymentInterval.data && data.paymentInterval.data.type === 'halfYearly' ? data.paymentInterval.data.offsetDay : 0,
                offsetMonth: data.paymentInterval.data && data.paymentInterval.data.type === 'halfYearly' ? data.paymentInterval.data.offsetMonth : 0,
            },
            yearly: {
                day: data.paymentInterval.data && data.paymentInterval.data.type === 'yearly' ? data.paymentInterval.data.day : 1,
                month: data.paymentInterval.data && data.paymentInterval.data.type === 'yearly' ? data.paymentInterval.data.month : 1,
            },
        };
        clubFee.chargeCredit = data.chargeCredit;
        clubFee.priceHistory = priceHistory;
        clubFee.isDeletable = data.isDeletable;
        clubFee.canEdit = data.canEdit;
        clubFee.canDelete = data.canDelete;

        return clubFee;
    }

    constructor() {
        this.id = null;
        this.name = '';
        this.priceHistory = null;
        this.paymentApproach = 'undefined';
        this.isArchived = false;
        this.clubCostCenter = null;
        this.impersonalAccount = null;
        this.category = null;
        this.department = null;
        this.basePrice = null;
        this.rollingIntervalType = null;
        this.invoiceTemplateType = 'invoice_default';
        this.paymentInterval = {
            type: '',
            monthly: {
                day: 1,
            },
            monthlyEnd: {
                offset: 0,
            },
            quarterly: {
                offsetDay: 0,
                offsetMonth: 0,
            },
            halfYearly: {
                offsetDay: 0,
                offsetMonth: 0,
            },
            yearly: {
                day: 1,
                month: 1,
            },
        };
        this.chargeCredit = false;
        this.priceHistory = null;
        this.note = '';
        this.isDeletable = true;
        this.canEdit = false;
        this.canDelete = false;
    }

    serialize() {
        const payload = {
            name: this.name,
            feeCategoryId: this.category != null ? this.category.id : null,
            departmentId: this.department != null ? this.department.id : null,
            clubCostCenterId: this.clubCostCenter != null ? this.clubCostCenter.id : null,
            impersonalAccountId: this.impersonalAccount != null ? this.impersonalAccount.id : null,
            chargeCredit: this.chargeCredit,
            invoiceTemplateType: this.invoiceTemplateType,
            price: {
                netOrGross: this.basePrice.netOrGross,
                amount: {
                    numerator: Math.round(this.basePrice.amount * 100),
                    denominator: 100,
                },
                vat: {
                    numerator: this.basePrice.vat ? this.basePrice.vat * 100 : 0,
                    denominator: 10000,
                },
                currency: this.basePrice.currency,
                type: this.basePrice.type.value,
            },
            note: this.note ?? '',
        };
        if (this.paymentApproach !== 'undefined') {
            payload.paymentApproach = this.paymentApproach;
        }
        if (this.paymentInterval.type !== '') {
            payload.paymentInterval = {
                type: this.paymentInterval.type,
            };
            switch (this.paymentInterval.type) {
                case 'monthly':
                    payload.paymentInterval.day = this.paymentInterval.monthly.day.value;
                    break;

                case 'monthlyEnd':
                    payload.paymentInterval.offset = this.paymentInterval.monthlyEnd.offset.value;
                    break;

                case 'quarterly':
                    payload.paymentInterval.offsetDay = this.paymentInterval.quarterly.offsetDay.value;
                    payload.paymentInterval.offsetMonth = this.paymentInterval.quarterly.offsetMonth.value;
                    break;

                case 'halfYearly':
                    payload.paymentInterval.offsetDay = this.paymentInterval.halfYearly.offsetDay.value;
                    payload.paymentInterval.offsetMonth = this.paymentInterval.halfYearly.offsetMonth.value;
                    break;

                case 'yearly':
                    payload.paymentInterval.day = this.paymentInterval.yearly.day.value;
                    payload.paymentInterval.month = this.paymentInterval.yearly.month.value;
                    break;

                default:
                    throw new Error(`Payment interval "${this.paymentInterval.type}" does not exist.`);
            }
        }
        payload.rollingIntervalType = this.rollingIntervalType?.value;
        return payload;
    }
}
